import { Context } from '../context/Context';
import { Camera } from '../camera';
import { FullscreenQuad } from './FullscreenQuad';
import { GBuffer } from './GBuffer';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';
import { ProceduralSky } from '../object/primitives/ProceduralSky';
import { DirectionalLight } from '../light';
import { vec3 } from 'gl-matrix';

// Aerial perspective: blend opaque scene pixels toward the same atmospheric
// scattering colour the procedural sky dome shows in their direction, with
// strength rising exponentially with view distance. Smooths the LOD3 ring's
// silhouette drift and the hard horizon clip into a continuous distance haze.
export class AerialPerspectivePass {
  private scratchFBO!: WebGLFramebuffer;
  private scratchTexture!: RenderTexture;
  private width!: number;
  private height!: number;

  // Tunables — calibrated for km-scale outdoor scenes (Demo: 2600m terrain).
  // Density picked so haze reaches ~0.95 by ~2km past startDistance, i.e. fully
  // covers the LOD3 ring boundary so silhouette pops between rings wash out.
  public density = 0.0015;
  public startDistance = 200;
  public maxBlend = 0.95;

  init(ctx: Context): void {
    const gl = ctx.gl;
    this.width = ctx.viewPort.width;
    this.height = ctx.viewPort.height;

    this.scratchTexture = new RenderTexture();
    this.scratchTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA16F,
      gl.RGBA,
      gl.HALF_FLOAT,
    );
    this.scratchFBO = createFramebuffer(gl, this.scratchTexture.glTexture!);
  }

  render(
    ctx: Context,
    gBuffer: GBuffer,
    quad: FullscreenQuad,
    camera: Camera,
    sky: ProceduralSky,
    sunLight: DirectionalLight | undefined,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    const gl = ctx.gl;

    // Sun direction TOWARD the sun in world space — same convention as
    // ProceduralSky.sunScratch derivation.
    const sunDir = vec3.create();
    if (sunLight) {
      sunDir[0] = -sunLight.direction[0];
      sunDir[1] = -sunLight.direction[1];
      sunDir[2] = -sunLight.direction[2];
    } else {
      sunDir[0] = sky.sunDirection[0];
      sunDir[1] = sky.sunDirection[1];
      sunDir[2] = sky.sunDirection[2];
    }
    vec3.normalize(sunDir, sunDir);

    // 1. Copy current opaque colour into the scratch texture so the shader
    //    can sample it while writing back to the same target.
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, outputBuffer);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.scratchFBO);
    gl.blitFramebuffer(
      0, 0, ctx.viewPort.width, ctx.viewPort.height,
      0, 0, this.width, this.height,
      gl.COLOR_BUFFER_BIT, gl.NEAREST,
    );

    // 2. Composite haze back into the opaque target.
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    const shader = ctx.engine.shaderManager.getAerialPerspectiveShader();
    ctx.setShader(shader);

    shader.uniformsContainer
      .setColorTexture(this.scratchTexture)
      .setDepthTexture(gBuffer.depthTexture)
      .setInvViewProjection(camera.invViewProjection)
      .setCameraPosition(camera.cameraPosition as number[])
      .setSunDirection(sunDir as unknown as number[])
      .setSunIntensity(sky.sunIntensity)
      .setTintColor(sky.tintColor)
      .setFogColor(sky.fogColor)
      .setHorizonMist(sky.horizonMist)
      .setExposure(sky.exposure)
      .setDensity(this.density)
      .setStartDistance(this.startDistance)
      .setMaxBlend(this.maxBlend);

    shader.validate();
    quad.draw(gl);
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.scratchFBO) gl.deleteFramebuffer(this.scratchFBO);
    if (this.scratchTexture?.glTexture) gl.deleteTexture(this.scratchTexture.glTexture);
  }
}
